import { useEffect } from "react";

/**
 * SecurityHeaders Component
 *
 * Sets security-related meta tags and headers for the application
 * This helps prevent XSS, clickjacking, and other security vulnerabilities
 */
export function SecurityHeaders() {
  useEffect(() => {
    // Set security-related meta tags
    const setMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Prevent clickjacking
    setMetaTag('frame-options', 'DENY');
    setMetaTag('X-Frame-Options', 'DENY');

    // Prevent MIME type sniffing
    setMetaTag('X-Content-Type-Options', 'nosniff');

    // Control referrer information
    setMetaTag('Referrer-Policy', 'strict-origin-when-cross-origin');

    // XSS Protection (for older browsers)
    setMetaTag('X-XSS-Protection', '1; mode=block');

    // Content Security Policy (basic implementation)
    const cspContent = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval'", // Remove unsafe-inline/eval when CSP is mature
      "style-src 'self' 'unsafe-inline'",
      "img-src 'self' data: https:",
      "font-src 'self'",
      "connect-src 'self'",
      "frame-src 'none'",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self'",
    ].join('; ');

    setMetaTag('Content-Security-Policy', cspContent);

    // CSRF token meta tag for JavaScript access
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_token='))
      ?.split('=')[1];

    if (csrfToken) {
      let csrfMeta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
      if (!csrfMeta) {
        csrfMeta = document.createElement('meta');
        csrfMeta.name = 'csrf-token';
        document.head.appendChild(csrfMeta);
      }
      csrfMeta.content = csrfToken;
    }

    // Clear sensitive data on page unload
    const handleBeforeUnload = () => {
      // Clear any sensitive data from memory
      // Note: performance.memory may not be available in all browsers
      if ('memory' in (window.performance || {})) {
        // Trigger garbage collection hint (not guaranteed)
        if ((window as any).gc) {
          (window as any).gc();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // This component doesn't render anything visible
  return null;
}

// Hook for security checks
export const useSecurityCheck = () => {
  const checkHTTPS = () => {
    return window.location.protocol === 'https:' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  };

  const checkSecureContext = () => {
    return window.isSecureContext;
  };

  const warnIfInsecure = () => {
    if (!checkHTTPS()) {
      console.warn('⚠️ Warning: Application is running over HTTP. Sensitive data may be compromised.');
    }

    if (!checkSecureContext()) {
      console.warn('⚠️ Warning: Application is not in a secure context. Some security features may be disabled.');
    }
  };

  useEffect(() => {
    warnIfInsecure();
  }, []);

  return {
    isHTTPS: checkHTTPS(),
    isSecureContext: checkSecureContext(),
    warnIfInsecure,
  };
};